import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart, PGBarChart } from '../../components/primitives/PGChart';
import { GROUP, BRANCHES, REGIONS, TRENDS, MONTHS } from '../../utils/orgData';
import { PG } from '../../utils/theme';
import { useLiveFeed } from '../../components/live/LiveFeedProvider';

const { FiUsers, FiUserX, FiLogOut, FiBookOpen, FiShield } = FiIcons;

const absenteeismTone = (v) => (v > 5 ? PG.red : v >= 3 ? PG.amber : PG.emerald);

export default function HRSliver() {
  const feed = useLiveFeed();
  const hr = GROUP.kpis.hr;

  // Stacked headcount by region (split into technicians / admin mock)
  const regionCategories = REGIONS.map((r) => r.name);
  const techSeries = REGIONS.map((r) => Math.round(r.kpis.hr.headcount * 0.72));
  const adminSeries = REGIONS.map((r) => r.kpis.hr.headcount - Math.round(r.kpis.hr.headcount * 0.72));

  // Absenteeism sorted worst first
  const absData = [...BRANCHES]
    .sort((a, b) => b.hr.absenteeism - a.hr.absenteeism)
    .slice(0, 12);

  const disciplinary = BRANCHES.filter((b) => b.hr.disciplinaryOpen > 0).map((b) => ({
    ...b,
    owner: 'HR Mgr',
    ageDays: 5 + Math.floor(Math.random() * 20),
  }));
  // ensure at least a couple of rows
  if (disciplinary.length < 2) {
    disciplinary.push(
      { id: BRANCHES[2].id, name: BRANCHES[2].name, country: BRANCHES[2].country, hr: { disciplinaryOpen: 1 }, owner: 'HR Mgr', ageDays: 7 },
      { id: BRANCHES[6].id, name: BRANCHES[6].name, country: BRANCHES[6].country, hr: { disciplinaryOpen: 1 }, owner: 'HR Mgr', ageDays: 14 }
    );
  }

  const top5 = [...BRANCHES].sort((a, b) => b.hr.engagementScore - a.hr.engagementScore).slice(0, 5);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      {/* KPI row */}
      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="HEADCOUNT" value={hr.headcount} unit="FTE" icon={FiUsers} accent={PG.white} target="500" trend="up" trendValue="+2.1%" animated="ticker" numericValue={hr.headcount} tickerDigits={4} />
        <KPICard title="ABSENTEEISM" value={hr.absenteeism} unit="%" icon={FiUserX} accent={absenteeismTone(hr.absenteeism)} target="<3%" trend="down" trendValue="-0.4pp" sparkValues={feed.wipAgeP90Mins} />
        <KPICard title="TURNOVER YTD" value={hr.turnoverYTD} unit="%" icon={FiLogOut} accent={PG.amber} target="<8%" trend="down" trendValue="-1.2pp" />
        <KPICard title="TRAINING COMPLETE" value={hr.trainingComplete} unit="%" icon={FiBookOpen} accent={PG.emerald} target=">90%" trend="up" trendValue="+3pp" />
        <KPICard title="LTIFR" value={hr.ltifr} unit="" icon={FiShield} accent={PG.red} target="<1.0" trend="flat" trendValue="stable" animated="splitflap" flapText={String(hr.ltifr).padStart(3, '0')} flapWidth={3} />
      </div>

      {/* Main 12-col grid */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="HEADCOUNT_BY_REGION" subtitle="Technicians vs Admin (FTE)" className="col-span-4">
          <div className="h-full p-2">
            <PGBarChart
              categories={regionCategories}
              series={[
                { name: 'Technicians', data: techSeries, stack: 'hc', itemStyle: { color: PG.red } },
                { name: 'Admin', data: adminSeries, stack: 'hc', itemStyle: { color: PG.neutral600 } },
              ]}
            />
          </div>
        </Panel>

        <Panel title="ABSENTEEISM_BY_BRANCH" subtitle="worst → best, target <3%" className="col-span-4">
          <div className="h-full p-2">
            <PGBarChart
              horizontal
              categories={absData.map((b) => b.name).reverse()}
              series={[
                {
                  name: 'Absenteeism %',
                  data: absData
                    .map((b) => ({
                      value: b.hr.absenteeism,
                      itemStyle: { color: absenteeismTone(b.hr.absenteeism) },
                    }))
                    .reverse(),
                },
              ]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel title="12_MO_TURNOVER_TREND" subtitle="rolling annualised" className="col-span-4" livePulse>
          <div className="h-full p-2">
            <PGLineChart
              categories={MONTHS}
              series={[{ name: 'Turnover %', data: TRENDS.groupTurnover, itemStyle: { color: PG.amber }, lineStyle: { color: PG.amber, width: 2 } }]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel
          title="BRANCHES_WITH_OPEN_DISCIPLINARY"
          subtitle={`${disciplinary.length} active`}
          right={<StatusPill tone="alert" pulse>ACTION</StatusPill>}
          className="col-span-7"
          tone={disciplinary.length > 0 ? 'alert' : 'default'}
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs">
              <thead className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Branch</th>
                  <th className="text-left px-4 py-2">Country</th>
                  <th className="text-center px-4 py-2">Open</th>
                  <th className="text-left px-4 py-2">Owner</th>
                  <th className="text-right px-4 py-2">Age</th>
                </tr>
              </thead>
              <tbody>
                {disciplinary.map((b, i) => (
                  <tr
                    key={b.id + i}
                    className="row-clickable cursor-pointer border-t border-white/5 hover:bg-[#D71921]/10 transition-colors"
                    onClick={() => { window.location.hash = `#/sliver/hr/branch/${b.id}`; }}
                  >
                    <td className="px-4 py-2">
                      <Link
                        to={`/sliver/hr/branch/${b.id}`}
                        className="text-white hover:text-[#D71921] font-medium"
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        {b.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-neutral-400">{b.country}</td>
                    <td className="px-4 py-2 text-center font-doto text-lg text-[#D71921]">{b.hr.disciplinaryOpen}</td>
                    <td className="px-4 py-2 text-neutral-400 font-mono-tech text-[10px]">{b.owner}</td>
                    <td className="px-4 py-2 text-right font-mono-tech text-[10px] text-neutral-500">{b.ageDays}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="ENGAGEMENT_SCORE_TOP5" subtitle="click to drill" right={<FiIcons.FiAward className="text-[#FFB800]" />} className="col-span-5">
          <div className="h-full overflow-y-auto custom-scrollbar p-2">
            {top5.map((b, i) => (
              <Link
                key={b.id}
                to={`/sliver/hr/branch/${b.id}`}
                className="flex items-center justify-between px-3 py-2 border-b border-white/5 hover:bg-[#D71921]/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-doto text-2xl text-[#FFB800] w-6">{i + 1}</span>
                  <div>
                    <div className="text-white text-sm font-medium">{b.name}</div>
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">{b.country}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-doto text-2xl text-white">{b.hr.engagementScore}</div>
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">/ 10</div>
                </div>
              </Link>
            ))}
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
